import clsx from 'clsx';
import {
  ChevronLeft,
  ChevronRight,
  Eraser,
  Hand,
  Highlighter,
  PenLine,
  RotateCw,
  StickyNote,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  PanelRightClose,
  PanelRightOpen,
  ArrowLeft,
  MoreHorizontal,
  MessageCircle,
  NotebookPen,
} from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { StudyPdfFullscreenButton } from './StudyPdfFullscreenButton';
import type { usePdfFullscreen } from './usePdfFullscreen';
import { PDF_COLOURS, PDF_PEN_WIDTHS, type PdfColour, type PdfTool } from './studyPdfPalette';

export const pdfIconButton =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-hairline bg-canvas-pure text-ink transition hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky disabled:cursor-not-allowed disabled:opacity-35';

function Control({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={pdfIconButton}
    >
      {children}
    </button>
  );
}

export function StudyPdfToolbar({
  page,
  pages,
  zoom,
  loading,
  locked,
  drawing,
  tool,
  colour,
  width,
  canUndo,
  canRedo,
  fullscreen,
  notesOpen,
  onPage,
  onZoom,
  onRotate,
  onHistory,
  onTool,
  onColour,
  onWidth,
  onToggleNotes,
  fileName,
  panel,
  onPanel,
  onBack,
  mobileLearningAction,
}: {
  page: number;
  pages: number;
  zoom: number;
  loading: boolean;
  locked: boolean;
  drawing: boolean;
  tool: PdfTool;
  colour: PdfColour;
  width: number;
  canUndo: boolean;
  canRedo: boolean;
  fullscreen: ReturnType<typeof usePdfFullscreen>;
  notesOpen: boolean;
  onPage: (value: number) => void;
  onZoom: (value: number) => void;
  onRotate: () => void;
  onHistory: (direction: 'undo' | 'redo') => void;
  onTool: (value: PdfTool) => void;
  onColour: (value: PdfColour) => void;
  onWidth: (value: number) => void;
  onToggleNotes: () => void;
  fileName: string;
  panel: 'notes' | 'tutor';
  onPanel: (value: 'notes' | 'tutor') => void;
  onBack?: () => void;
  mobileLearningAction?: ReactNode;
}) {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const tools: Array<[PdfTool, string, ComponentType<{ className?: string }>]> = [
    ['read', 'Read', Hand],
    ['ink', 'Draw', PenLine],
    ['highlight', 'Highlight', Highlighter],
    ['note', 'Note', StickyNote],
    ['erase', 'Erase', Eraser],
  ];
  return (
    <>
      <div className="order-first shrink-0 border-b border-hairline bg-canvas-pure pt-[env(safe-area-inset-top)] md:pt-0">
        <div className="flex h-14 items-center gap-2 px-2 md:hidden">
          {onBack && (
            <Control label="Back to Study" disabled={locked} onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Control>
          )}
          <div className="min-w-0 flex-1 px-1">
            <p className="truncate text-xs font-bold text-ink">{fileName}</p>
            <p className="mt-1 text-[11px] text-slate2">
              Page {page} of {pages || '…'}
            </p>
          </div>
          <button
            type="button"
            aria-label="PDF view options"
            aria-expanded={optionsOpen}
            onClick={() => setOptionsOpen((open) => !open)}
            className={pdfIconButton}
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>
        <div
          className={clsx(
            optionsOpen ? 'flex' : 'hidden',
            'flex-wrap items-center justify-between gap-2 px-3 py-2 md:flex',
          )}
        >
          {optionsOpen && mobileLearningAction && (
            <fieldset disabled={locked || drawing || loading} className="w-full min-w-0 md:hidden">
              {mobileLearningAction}
            </fieldset>
          )}
          <div
            role="group"
            aria-label="PDF page controls"
            className="flex w-full min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-x-auto pb-1 sm:w-auto"
          >
            <Control
              label="Previous PDF page"
              disabled={loading || locked || page <= 1}
              onClick={() => onPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Control>
            <span className="min-w-14 text-center text-xs font-bold text-ink" aria-live="polite">
              {pages ? [page, pages].join(' / ') : 'Opening…'}
            </span>
            <Control
              label="Next PDF page"
              disabled={loading || locked || page >= pages}
              onClick={() => onPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Control>
            <span className="mx-1 hidden h-5 border-l border-hairline sm:block" />
            <Control
              label="Zoom PDF page out"
              disabled={loading || drawing || zoom <= 0.6}
              onClick={() => onZoom(Math.max(0.6, Math.round((zoom - 0.2) * 10) / 10))}
            >
              <ZoomOut className="h-4 w-4" />
            </Control>
            <span className="min-w-10 text-center text-xs font-bold text-slate2" aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <Control
              label="Zoom PDF page in"
              disabled={loading || drawing || zoom >= 2}
              onClick={() => onZoom(Math.min(2, Math.round((zoom + 0.2) * 10) / 10))}
            >
              <ZoomIn className="h-4 w-4" />
            </Control>
            <Control
              label="Rotate PDF page clockwise"
              disabled={loading || drawing}
              onClick={onRotate}
            >
              <RotateCw className="h-4 w-4" />
            </Control>
            <Control
              label="Undo PDF annotation change"
              disabled={locked || !canUndo}
              onClick={() => onHistory('undo')}
            >
              <Undo2 className="h-4 w-4" />
            </Control>
            <Control
              label="Redo PDF annotation change"
              disabled={locked || !canRedo}
              onClick={() => onHistory('redo')}
            >
              <Redo2 className="h-4 w-4" />
            </Control>
          </div>
          <div className="flex items-center gap-1">
            <StudyPdfFullscreenButton control={fullscreen} />
            <button
              type="button"
              className={pdfIconButton}
              title={notesOpen ? 'Hide notes' : 'Show notes'}
              aria-label={notesOpen ? 'Hide notes' : 'Show notes'}
              aria-expanded={notesOpen}
              onClick={onToggleNotes}
            >
              {notesOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
      <div
        data-testid="pdf-bottom-tools"
        className="order-last z-20 shrink-0 border-t border-hairline bg-canvas-pure px-1 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:flex md:flex-wrap md:items-center md:justify-center md:gap-6 md:px-3 md:py-2"
      >
        <div
          role="group"
          aria-label="PDF annotation tools"
          className="flex w-full flex-nowrap justify-between gap-0 md:w-auto md:gap-1 md:rounded-2xl md:bg-surface md:p-1"
        >
          {tools.map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              aria-label={label}
              title={label}
              aria-pressed={tool === value}
              disabled={locked || loading}
              onClick={() => onTool(value)}
              className={clsx(
                'inline-flex min-h-12 min-w-[44px] flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-bold md:min-h-11 md:flex-none md:flex-row md:gap-1.5 md:px-3 md:text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky disabled:cursor-not-allowed disabled:opacity-40',
                tool === value
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink-soft hover:bg-canvas-pure',
              )}
            >
              <Icon className="h-5 w-5 md:h-4 md:w-4" />
              <span>{label}</span>
            </button>
          ))}
          {(
            [
              ['notes', 'Notes', NotebookPen],
              ['tutor', 'Tutor', MessageCircle],
            ] as const
          ).map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              aria-label={value === 'notes' ? 'Open page notes' : 'Ask Tutor about PDF'}
              aria-pressed={notesOpen && panel === value}
              onClick={() => onPanel(value)}
              className={clsx(
                'flex min-h-12 min-w-[44px] flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky md:hidden',
                notesOpen && panel === value ? 'bg-wash-sky text-ink' : 'text-slate2',
              )}
            >
              <Icon className="h-5 w-5 md:h-4 md:w-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>
        {(tool === 'ink' || tool === 'highlight') && (
          <div
            className={clsx(
              notesOpen ? 'hidden md:flex' : 'flex',
              'mt-2 items-center justify-between gap-1 border-t border-hairline-soft pt-1 md:mt-0 md:flex-wrap md:gap-3 md:border-0 md:pt-0',
            )}
          >
            <div role="group" aria-label="Annotation colour" className="flex items-center gap-1">
              {PDF_COLOURS.map((value) => (
                <button
                  key={value.id}
                  type="button"
                  aria-label={value.label + ' annotation colour'}
                  aria-pressed={colour === value.id}
                  disabled={locked || drawing}
                  onClick={() => onColour(value.id)}
                  className={clsx(
                    'flex h-11 w-11 items-center justify-center rounded-xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky disabled:opacity-40',
                    colour === value.id
                      ? 'border-ink bg-surface'
                      : 'border-transparent hover:bg-surface',
                  )}
                >
                  <span
                    className={clsx('h-5 w-5 rounded-full ring-1 ring-ink/10', value.background)}
                  />
                </button>
              ))}
            </div>
            {tool === 'ink' && (
              <label className="flex items-center gap-2 text-xs font-bold text-slate2">
                <span className="hidden sm:inline">Pen width</span>
                <select
                  aria-label="Pen width"
                  value={width}
                  disabled={locked || drawing}
                  onChange={(event) => onWidth(Number(event.target.value))}
                  className="min-h-11 rounded-xl border border-hairline bg-canvas-pure px-2 text-ink"
                >
                  {PDF_PEN_WIDTHS.map((value) => (
                    <option key={value.value} value={value.value}>
                      {value.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>
    </>
  );
}
